import { supabase } from '../lib/supabase';

export const fatouReservationService = {
  /**
   * Get all reservations with filtering and pagination
   */
  async getReservations(filters = {}) {
    try {
      let query = supabase?.from('fatou_reservations')?.select('*, employees(id, name, phone)')?.order('created_at', { ascending: false });

      // Apply filters
      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }
      if (filters?.startDate) {
        query = query?.gte('created_at', filters?.startDate);
      }
      if (filters?.endDate) {
        query = query?.lte('created_at', filters?.endDate);
      }
      if (filters?.searchQuery) {
        query = query?.or(`customer_name.ilike.%${filters?.searchQuery}%,customer_phone.ilike.%${filters?.searchQuery}%,customer_email.ilike.%${filters?.searchQuery}%`);
      }
      if (filters?.confirmationSent !== undefined && filters?.confirmationSent !== null) {
        query = query?.eq('confirmation_sent', filters?.confirmationSent);
      }

      // Pagination
      if (filters?.limit) {
        query = query?.limit(filters?.limit);
      }
      if (filters?.offset) {
        query = query?.range(filters?.offset, filters?.offset + (filters?.limit || 50) - 1);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching reservations:', error);
      throw error;
    }
  },

  /**
   * Get single reservation by ID
   */
  async getReservationById(id) {
    try {
      const { data, error } = await supabase?.from('fatou_reservations')?.select('*, employees(id, name, phone)')?.eq('id', id)?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching reservation:', error);
      throw error;
    }
  },

  /**
   * Create new reservation
   */
  async createReservation(reservationData) {
    try {
      const { data, error } = await supabase?.from('fatou_reservations')?.insert({
          session_id: reservationData?.sessionId,
          customer_name: reservationData?.customerName,
          customer_phone: reservationData?.customerPhone,
          customer_email: reservationData?.customerEmail,
          pickup_address: reservationData?.pickupAddress,
          pickup_city: reservationData?.pickupCity,
          pickup_postal_code: reservationData?.pickupPostalCode,
          delivery_address: reservationData?.deliveryAddress,
          delivery_city: reservationData?.deliveryCity,
          delivery_country: reservationData?.deliveryCountry || 'Côte d\'Ivoire',
          collection_date: reservationData?.collectionDate,
          service_type: reservationData?.serviceType,
          item_description: reservationData?.itemDescription,
          special_instructions: reservationData?.specialInstructions,
          source: reservationData?.source || 'fatou_ia',
          quantity: reservationData?.quantity,
          price: reservationData?.price,
          status: 'pending'
        })?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating reservation:', error);
      throw error;
    }
  },

  /**
   * Update reservation status
   */
  async updateReservationStatus(id, status, adminNotes = null) {
    try {
      const updateData = { status };
      if (adminNotes) {
        updateData.admin_notes = adminNotes;
      }

      const { data, error } = await supabase?.from('fatou_reservations')?.update(updateData)?.eq('id', id)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating reservation status:', error);
      throw error;
    }
  },

  /**
   * Assign driver to reservation
   */
  async assignDriver(id, driverId, driverName) {
    try {
      const { data, error } = await supabase?.from('fatou_reservations')?.update({
        driver_id: driverId,
        assigned_driver_name: driverName,
        assigned_at: new Date()?.toISOString(),
        status: 'assigned'
      })?.eq('id', id)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error assigning driver:', error);
      throw error;
    }
  },

  /**
   * Update reservation details
   */
  async updateReservation(id, updates) {
    try {
      const updateData = {};
      
      if (updates?.customerName !== undefined) updateData.customer_name = updates?.customerName;
      if (updates?.customerPhone !== undefined) updateData.customer_phone = updates?.customerPhone;
      if (updates?.customerEmail !== undefined) updateData.customer_email = updates?.customerEmail;
      if (updates?.pickupAddress !== undefined) updateData.pickup_address = updates?.pickupAddress;
      if (updates?.pickupCity !== undefined) updateData.pickup_city = updates?.pickupCity;
      if (updates?.pickupPostalCode !== undefined) updateData.pickup_postal_code = updates?.pickupPostalCode;
      if (updates?.deliveryAddress !== undefined) updateData.delivery_address = updates?.deliveryAddress;
      if (updates?.deliveryCity !== undefined) updateData.delivery_city = updates?.deliveryCity;
      if (updates?.deliveryCountry !== undefined) updateData.delivery_country = updates?.deliveryCountry;
      if (updates?.collectionDate !== undefined) updateData.collection_date = updates?.collectionDate;
      if (updates?.serviceType !== undefined) updateData.service_type = updates?.serviceType;
      if (updates?.itemDescription !== undefined) updateData.item_description = updates?.itemDescription;
      if (updates?.specialInstructions !== undefined) updateData.special_instructions = updates?.specialInstructions;
      if (updates?.adminNotes !== undefined) updateData.admin_notes = updates?.adminNotes;
      if (updates?.status !== undefined) updateData.status = updates?.status;

      const { data, error } = await supabase?.from('fatou_reservations')?.update(updateData)?.eq('id', id)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating reservation:', error);
      throw error;
    }
  },

  /**
   * Mark confirmation as sent
   */
  async markConfirmationSent(id) {
    try {
      const { data, error } = await supabase?.from('fatou_reservations')?.update({
          confirmation_sent: true,
          confirmation_sent_at: new Date()?.toISOString()
        })?.eq('id', id)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error marking confirmation sent:', error);
      throw error;
    }
  },

  /**
   * Delete reservation
   */
  async deleteReservation(id) {
    try {
      const { error } = await supabase?.from('fatou_reservations')?.delete()?.eq('id', id);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting reservation:', error);
      throw error;
    }
  },

  /**
   * Get reservation statistics
   */
  async getReservationStats() {
    try {
      const { data: allReservations, error } = await supabase?.from('fatou_reservations')?.select('status, created_at, confirmation_sent');

      if (error) throw error;

      const stats = {
        totalReservations: allReservations?.length || 0,
        pendingCount: allReservations?.filter(r => r?.status === 'pending')?.length || 0,
        confirmedCount: allReservations?.filter(r => r?.status === 'confirmed')?.length || 0,
        processingCount: allReservations?.filter(r => r?.status === 'processing')?.length || 0,
        completedCount: allReservations?.filter(r => r?.status === 'completed')?.length || 0,
        cancelledCount: allReservations?.filter(r => r?.status === 'cancelled')?.length || 0,
        assignedCount: allReservations?.filter(r => r?.status === 'assigned')?.length || 0,
        confirmationSentCount: allReservations?.filter(r => r?.confirmation_sent)?.length || 0,
        todayCount: allReservations?.filter(r => {
          const resDate = new Date(r?.created_at);
          const today = new Date();
          return resDate?.toDateString() === today?.toDateString();
        })?.length || 0
      };

      return stats;
    } catch (error) {
      console.error('Error fetching reservation stats:', error);
      throw error;
    }
  },

  /**
   * Subscribe to real-time reservation changes
   */
  subscribeToReservations(callback) {
    const channel = supabase?.channel('fatou_reservations_changes')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'fatou_reservations'
        },
        (payload) => {
          callback(payload);
        }
      )?.subscribe();

    return channel;
  },

  /**
   * Unsubscribe from real-time changes
   */
  unsubscribe(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  }
};